/* eslint-disable react/display-name */
import React, { useState, useEffect } from 'react'
import { withTranslation } from 'react-i18next'
import { Box, Container, Modal, Button } from '@mui/material'
import AddRounded from '@mui/icons-material/AddRounded'
import { raisedButtonSx } from '../components/UI/Depth'
import { gql, useQuery, useMutation } from '@apollo/client'
import Header from '../components/Headers/Header'
import ZoneComponent from '../components/Zone/Zone'
import CustomLoader from '../components/Loader/CustomLoader'
import { getZones, deleteZone } from '../apollo'
import DataTable from '../components/DataTable'
import orderBy from 'lodash/orderBy'
import SearchBar from '../components/TableHeader/SearchBar'
import { customStyles } from '../utils/tableCustomStyles'
import useGlobalStyles from '../utils/globalStyles'
import { RowActionMenu } from '../components/UI/RowActionMenu'
import EditOutlined from '@mui/icons-material/EditOutlined'
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded'
import TableHeader from '../components/TableHeader'
import Alert from '../components/Alert'
import ConfigurableValues from '../config/constants'
import { modalOverlay, modalCard } from '../utils/modalStyles'

const GET_ZONES = gql`
  ${getZones}
`
const DELETE_ZONE = gql`
  ${deleteZone}
`

const Zones = props => {
  const { t } = props
  const { PAID_VERSION } = ConfigurableValues()
  const [editModal, setEditModal] = useState(false)
  const [addModal, setAddModal] = useState(false)
  const [zones, setZone] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const onChangeSearch = e => setSearchQuery(e.target.value)

  const [mutate, { error, loading }] = useMutation(DELETE_ZONE, {
    refetchQueries: [{ query: GET_ZONES }]
  })
  const { data, loading: loadingQuery, refetch } = useQuery(GET_ZONES)
  const toggleModal = zone => {
    setEditModal(!editModal)
    setZone(zone)
  }
  const closeEditModal = () => {
    setEditModal(false)
  }

  useEffect(() => {
    localStorage.removeItem('restaurant_id')
  }, [])

  const customSort = (rows, field, direction) => {
    const handleField = row => {
      if (row[field]) {
        return row[field].toLowerCase()
      }

      return row[field]
    }

    return orderBy(rows, handleField, direction)
  }

  const columns = [
    {
      name: t('Title'),
      sortable: true,
      selector: 'title'
    },
    {
      name: t('Description'),
      sortable: true,
      selector: 'description'
    },
    {
      name: t('Action'),
      cell: row => <>{actionButtons(row)}</>
    }
  ]
  const actionButtons = row => {
    return (
      <>
        <div>
          <RowActionMenu
            label={t('Action')}
            actions={[
              {
                label: t('Edit'),
                icon: EditOutlined,
                tone: 'primary',
                onClick: () => {
                  if (PAID_VERSION) toggleModal(row)
                  else {
                    setIsOpen(true)
                    setTimeout(() => {
                      setIsOpen(false)
                    }, 5000)
                  }
                }
              },
              {
                label: t('Delete'),
                icon: DeleteOutlineRounded,
                tone: 'danger',
                divider: true,
                confirm: {
                  title: `${t('Delete')}?`,
                  message: t('DeleteConfirm'),
                  cancelLabel: t('Cancel')
                },
                onClick: () => {
                  if (PAID_VERSION) mutate({ variables: { id: row._id } })
                  else {
                    setIsOpen(true)
                    setTimeout(() => {
                      setIsOpen(false)
                    }, 2000)
                  }
                }
              }
            ]}
          />
        </div>
      </>
    )
  }

  const regex =
    searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null

  const filtered =
    searchQuery.length < 3
      ? data && data.zones
      : data &&
        data.zones.filter(zone => {
          return (
            zone.title.toLowerCase().search(regex) > -1 ||
            zone.description.toLowerCase().search(regex) > -1
          )
        })

  const globalClasses = useGlobalStyles()

  return (
    <>
      <Header
        action={
          <Button
            startIcon={<AddRounded />}
            onClick={() => setAddModal(true)}
            sx={raisedButtonSx}>
            {`${t('Add')} ${t('Zone')}`}
          </Button>
        }
      />
      {/* Page content */}
      <Container className={globalClasses.flex} fluid>
        {/* Table */}
        {isOpen && (
          <Alert message={t('AvailableAfterPurchasing')} severity="warning" />
        )}
        {error ? <span>{`Error! ${error.message}`}</span> : null}
        {loading ? <CustomLoader /> : null}
        <DataTable
          subHeader={true}
          subHeaderComponent={
            <SearchBar
              value={searchQuery}
              onChange={onChangeSearch}
              onClick={() => refetch()}
            />
          }
          title={<TableHeader title={t('Zones')} />}
          columns={columns}
          data={filtered}
          pagination
          progressPending={loadingQuery}
          progressComponent={<CustomLoader />}
          sortFunction={customSort}
          defaultSortField="title"
          customStyles={customStyles}
          selectableRows
        />
        <Modal
          open={editModal}
          onClose={() => {
            toggleModal()
          }}
          sx={modalOverlay}>
          <Box sx={modalCard('lg')}>
            <ZoneComponent zone={zones} onClose={closeEditModal} />
          </Box>
        </Modal>
        <Modal
          open={addModal}
          onClose={() => setAddModal(false)}
          sx={modalOverlay}>
          <Box sx={modalCard('xl')}>
            <ZoneComponent onClose={() => setAddModal(false)} />
          </Box>
        </Modal>
      </Container>
    </>
  )
}

export default withTranslation()(Zones)
